import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('atolyekirpi_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 403 && error.response?.data?.mustChangePassword) {
            sessionStorage.setItem('atolyekirpi_mcp', '1');
            localStorage.removeItem('atolyekirpi_user');
            if (window.location.pathname !== '/admin/login') {
                window.location.href = '/admin/login';
            }
        }
        if (error.response?.status === 401) {
            localStorage.removeItem('atolyekirpi_token');
            localStorage.removeItem('atolyekirpi_user');
            sessionStorage.removeItem('atolyekirpi_mcp');
            if (window.location.pathname.startsWith('/admin')) {
                window.location.href = '/admin/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    me: () => api.get('/auth/me'),
    changePassword: (data) => api.post('/auth/change-password', data)
};

// Categories
export const categoryAPI = {
    getAll: (params) => api.get('/categories', { params }),
    getById: (id) => api.get(`/categories/${id}`),
    create: (data) => api.post('/categories', data, data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    update: (id, data) => api.put(`/categories/${id}`, data, data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    delete: (id, config) => api.delete(`/categories/${id}`, config)
};

// Products
export const productAPI = {
    getAll: (params) => api.get('/products', { params }),
    getBrands: (params) => api.get('/products/brands', { params }),
    getBySlug: (slug) => api.get(`/products/slug/${slug}`),
    getById: (id) => api.get(`/products/${id}`),
    create: (formData) => api.post('/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/products/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/products/${id}`),
    deleteImage: (imageId) => api.delete(`/products/images/${imageId}`),
    deleteVariantImage: (imageId) => api.delete(`/products/variants/images/${imageId}`),
    toggleActive: (id) => api.patch(`/products/${id}/toggle`),
    excelTemplate: () => api.get('/products/excel/template', { responseType: 'blob' }),
    excelExport: () => api.get('/products/excel/export', { responseType: 'blob' }),
    excelPreview: (formData) => api.post('/products/excel/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    excelImport: (formData) => api.post('/products/excel/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    cloneProduct: (id) => api.post(`/products/${id}/clone`),
    reorderImages: (imageIds) => api.put('/products/images/reorder', { imageIds }),
    reorderVariantImages: (imageIds) => api.put('/products/variants/images/reorder', { imageIds }),
    getAccessories: (id) => api.get(`/products/${id}/accessories`),
    setAccessories: (id, accessoryIds) => api.put(`/products/${id}/accessories`, { accessoryIds })
};

// Stock
export const stockAPI = {
    getMovements: (params) => api.get('/stock/movements', { params }),
    addMovement: (data) => api.post('/stock/movements', data),
    getReport: () => api.get('/stock/report'),
    exportExcel: () => api.get('/stock/export', { responseType: 'blob' })
};

// Dashboard
export const dashboardAPI = {
    getSummary: () => api.get('/dashboard'),
    getNotifications: () => api.get('/dashboard/notifications')
};

// Sliders
export const sliderAPI = {
    getAll: (params) => api.get('/sliders', { params }),
    getById: (id) => api.get(`/sliders/${id}`),
    create: (formData) => api.post('/sliders', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/sliders/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/sliders/${id}`)
};

// Promo Banners
export const promoBannerAPI = {
    getAll: (params) => api.get('/promo-banners', { params }),
    getById: (id) => api.get(`/promo-banners/${id}`),
    create: (formData) => api.post('/promo-banners', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/promo-banners/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/promo-banners/${id}`)
};

// Testimonials
export const testimonialAPI = {
    getAll: (params) => api.get('/testimonials', { params }),
    getById: (id) => api.get(`/testimonials/${id}`),
    create: (data) => api.post('/testimonials', data),
    update: (id, data) => api.put(`/testimonials/${id}`, data),
    delete: (id) => api.delete(`/testimonials/${id}`)
};

// Site Settings
export const siteSettingsAPI = {
    getAll: () => api.get('/site-settings'),
    update: (data) => api.put('/site-settings', data),
    testTelegram: () => api.post('/site-settings/telegram-test')
};

// Serial Numbers
export const serialAPI = {
    getSerialNumbers: (params) => api.get('/serial/numbers', { params }),
    getSerialNumberById: (id) => api.get(`/serial/numbers/${id}`),
    createSerialNumbers: (data) => api.post('/serial/numbers', data),
    updateStatus: (id, data) => api.put(`/serial/numbers/${id}/status`, data),
    getHistory: (id) => api.get(`/serial/numbers/${id}/history`),
    getByProduct: (productId, params) => api.get(`/serial/product/${productId}`, { params }),
    scan: (data) => api.post('/serial/scan', data),
    processScan: (data) => api.post('/serial/scan/process', data),
};

// Platforms
export const platformAPI = {
    getAll: (params) => api.get('/platforms', { params }),
    create: (data) => api.post('/platforms', data),
    update: (id, data) => api.put(`/platforms/${id}`, data),
    delete: (id) => api.delete(`/platforms/${id}`)
};

// Sales
export const saleAPI = {
    getAll: (params) => api.get('/sales', { params }),
    getById: (id) => api.get(`/sales/${id}`),
    create: (data) => api.post('/sales', data),
    update: (id, data) => api.put(`/sales/${id}`, data),
    getStats: () => api.get('/sales/stats'),
    exportExcel: (params) => api.get('/sales/export', { params, responseType: 'blob' }),
    resetByProduct: (productId) => api.delete(`/sales/product/${productId}`)
};

// Users
export const userAPI = {
    getAll: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    changePassword: (id, data) => api.put(`/users/${id}/password`, data)
};

// Media Library
export const mediaAPI = {
    getAll: (params) => api.get('/media', { params }),
    upload: (formData, onUploadProgress) => api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress
    }),
    getUsage: (id) => api.get(`/media/${id}/usage`),
    delete: (id) => api.delete(`/media/${id}`),
    bulkDelete: (ids) => api.post('/media/bulk-delete', { ids })
};

// Customer Auth
const customerApi = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' }
});

customerApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('atolyekirpi_customer_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    const sessionId = localStorage.getItem('atolyekirpi_session_id');
    if (sessionId) {
        config.headers['x-session-id'] = sessionId;
    }
    return config;
});

customerApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const path = window.location.pathname;
            if (path.startsWith('/hesabim') || path.startsWith('/checkout')) {
                localStorage.removeItem('atolyekirpi_customer_token');
                localStorage.removeItem('atolyekirpi_customer');
                window.location.href = '/giris';
            }
        }
        return Promise.reject(error);
    }
);

export const customerAuthAPI = {
    register: (data) => customerApi.post('/customer/register', data),
    login: (data) => customerApi.post('/customer/login', data),
    googleLogin: (data) => customerApi.post('/customer/google', data),
    me: () => customerApi.get('/customer/me'),
    verifyEmail: (data) => customerApi.post('/customer/verify-email', data),
    resendVerification: () => customerApi.post('/customer/resend-verification'),
    updateProfile: (data) => customerApi.put('/customer/profile', data),
    changePassword: (data) => customerApi.put('/customer/password', data),
    getAddresses: () => customerApi.get('/customer/addresses'),
    createAddress: (data) => customerApi.post('/customer/addresses', data),
    updateAddress: (id, data) => customerApi.put(`/customer/addresses/${id}`, data),
    deleteAddress: (id) => customerApi.delete(`/customer/addresses/${id}`),
    getOrders: () => customerApi.get('/customer/orders'),
    getOrderDetail: (id) => customerApi.get(`/customer/orders/${id}`),
    forgotPassword: (data) => customerApi.post('/customer/forgot-password', data),
    resetPassword: (data) => customerApi.post('/customer/reset-password', data)
};

export const cartAPI = {
    get: () => customerApi.get('/cart'),
    add: (data) => customerApi.post('/cart/add', data),
    updateItem: (itemId, data) => customerApi.put(`/cart/item/${itemId}`, data),
    removeItem: (itemId) => customerApi.delete(`/cart/item/${itemId}`),
    clear: () => customerApi.delete('/cart/clear'),
    merge: () => customerApi.post('/cart/merge')
};

export const checkoutAPI = {
    initPayment: (data) => customerApi.post('/checkout/init', data),
    placeOrder: (data) => customerApi.post('/checkout/place-order', data)
};

export const legalPageAPI = {
    getAll: () => api.get('/legal'),
    getBySlug: (slug) => api.get(`/legal/${slug}`),
    getAllAdmin: () => api.get('/legal/admin/all'),
    create: (data) => api.post('/legal/admin', data),
    update: (id, data) => api.put(`/legal/admin/${id}`, data),
    delete: (id) => api.delete(`/legal/admin/${id}`)
};

export const orderAPI = {
    getAll: (params) => api.get('/checkout/orders', { params }),
    getById: (id) => api.get(`/checkout/orders/${id}`),
    updateStatus: (id, data) => api.put(`/checkout/orders/${id}`, data),
    uploadInvoice: (id, formData) => api.post(`/checkout/orders/${id}/invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
};

export const couponAPI = {
    getAll: (params) => api.get('/coupons', { params }),
    getById: (id) => api.get(`/coupons/${id}`),
    create: (data) => api.post('/coupons', data),
    update: (id, data) => api.put(`/coupons/${id}`, data),
    toggleActive: (id) => api.patch(`/coupons/${id}/toggle`),
    delete: (id) => api.delete(`/coupons/${id}`),
    validate: (data) => customerApi.post('/coupons/validate', data)
};

export const reviewAPI = {
    getProductReviews: (productId) => api.get(`/reviews/product/${productId}`),
    getReviewable: () => customerApi.get('/reviews/reviewable'),
    create: (data) => customerApi.post('/reviews', data),
    update: (id, data) => customerApi.put(`/reviews/${id}`, data),
    delete: (id) => customerApi.delete(`/reviews/${id}`),
    adminGetAll: (params) => api.get('/reviews/admin', { params }),
    adminApprove: (id, data) => api.put(`/reviews/admin/${id}/approve`, data),
    adminDelete: (id) => api.delete(`/reviews/admin/${id}`)
};

export const favoritesAPI = {
    getAll: () => customerApi.get('/favorites'),
    toggle: (productId) => customerApi.post('/favorites/toggle', { productId }),
    check: (ids) => customerApi.get(`/favorites/check?ids=${ids.join(',')}`),
    remove: (id) => customerApi.delete(`/favorites/${id}`)
};

// Blog API
export const blogAPI = {
    getAll: (params) => api.get('/blogs', { params }),
    getById: (id) => api.get(`/blogs/${id}`),
    getBySlug: (slug) => api.get(`/blogs/slug/${slug}`),
    create: (formData) => api.post('/blogs', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/blogs/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/blogs/${id}`),
};

// Blog Category API
export const blogCategoryAPI = {
    getAll: () => api.get('/blog-categories'),
    getById: (id) => api.get(`/blog-categories/${id}`),
    create: (data) => api.post('/blog-categories', data),
    update: (id, data) => api.put(`/blog-categories/${id}`, data),
    delete: (id) => api.delete(`/blog-categories/${id}`),
};

// Service API
export const serviceAPI = {
    getAll: (params) => api.get('/services', { params }),
    getById: (id) => api.get(`/services/${id}`),
    getBySlug: (slug) => api.get(`/services/slug/${slug}`),
    create: (formData) => api.post('/services', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/services/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateOrder: (data) => api.put('/services/order/update', data),
    delete: (id) => api.delete(`/services/${id}`),
};

// Corporate Page API
export const corporatePageAPI = {
    getAll: () => api.get('/corporate-pages'),
    getById: (id) => api.get(`/corporate-pages/${id}`),
    getBySlug: (slug) => api.get(`/corporate-pages/slug/${slug}`),
    create: (formData) => api.post('/corporate-pages', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/corporate-pages/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/corporate-pages/${id}`),
};

// Product Questions (Q&A)
export const productQuestionAPI = {
    getProductQuestions: (productId) => api.get(`/product-questions/product/${productId}`),
    create: (data) => customerApi.post('/product-questions', data),
    adminGetAll: (params) => api.get('/product-questions/admin', { params }),
    adminAnswer: (id, data) => api.put(`/product-questions/admin/${id}/answer`, data),
    adminDelete: (id) => api.delete(`/product-questions/admin/${id}`)
};

// Popup Banner API
export const popupBannerAPI = {
    get: () => api.get('/popup-banner'),
    update: (data) => api.put('/popup-banner', data, data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {})
};


// Homepage Categories API
export const homepageCategoryAPI = {
    getAll: () => api.get('/homepage-categories'),
    update: (id, data) => api.put(`/homepage-categories/${id}`, data, data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {})
};

// Page SEO API
export const pageSeoAPI = {
    getAll: () => api.get('/page-seo'),
    getByPage: (pageType, pageSlug) => api.get(`/page-seo/${pageType}${pageSlug ? '/' + pageSlug : ''}`),
    createOrUpdate: (data) => api.post('/page-seo', data),
    delete: (id) => api.delete(`/page-seo/${id}`),
};

export const IMAGE_BASE = '';
export { customerApi };
export default api;
